import { useEffect } from "react";
import { supabase } from "@/lib/supabase";
import i18n from "@/i18n/index";

const CMS_TO_I18N_MAP: Record<string, Record<string, string>> = {
  // Home page sections
  "home__hero": { title: "hero.title", subtitle: "hero.subtitle" },
  "home__mission": { title: "mission.title", body: "mission.body1", cta: "mission.cta" },
  "home__impact": { title: "impact.title", subtitle: "impact.subtitle" },
  "home__impact_stats": { stats: "impact.stats" },
  "home__projects_preview": { title: "projects.title", subtitle: "projects.subtitle", cta: "projects.viewAll" },
  "home__volunteer_preview": { title: "volunteer.title", body: "volunteer.body1", cta: "volunteer.cta" },
  "home__donate_preview": { tag: "donate.tag", title: "donate.title", subtitle: "donate.subtitle" },
  "home__photo_slider": { title: "photoSlider.title", subtitle: "photoSlider.subtitle" },
  "home__stories_preview": { title: "storiesPreview.title", subtitle: "storiesPreview.subtitle", cta: "storiesPreview.viewAll" },
  "home__stats": { stats: "hero.stats", visible: "hero.statsVisible" },

  // Mission page sections
  "mission__hero": { title: "missionPage.tag", subtitle: "missionPage.subtitle" },
  "mission__vision": { title: "missionPage.who.title", body: "missionPage.who.body1" },
  "mission__values": { title: "missionPage.values.title" },
  "mission__cta": { title: "missionPage.cta.title", subtitle: "missionPage.cta.subtitle", cta: "missionPage.cta.donate" },

  // Projects page sections
  "projects__hero": { title: "projectsPage.title", subtitle: "projectsPage.subtitle" },

  // Volunteer page sections
  "volunteer__hero": {
    title: "volunteerPage.title",
    subtitle: "volunteerPage.subtitle",
    body: "volunteerPage.tag",
    cta: "volunteerPage.applyNow",
  },
  "volunteer__opportunities": {
    title: "volunteerPage.positions.title",
    subtitle: "volunteerPage.positions.tag",
  },
  "volunteer__testimonials": {
    title: "volunteerPage.stories.title",
    subtitle: "volunteerPage.stories.tag",
  },

  // Donate page sections
  "donate__hero": { title: "donatePage.title", subtitle: "donatePage.subtitle" },
  "donate__tiers": { title: "donatePage.yourImpact" },
  "donate__how_to": { title: "donatePage.howToDonate", body: "donatePage.howToBody" },
  "donate__cta": { title: "donatePage.bottom.title", subtitle: "donatePage.bottom.subtitle", cta: "donatePage.bottom.contact" },

  // Contact page sections
  "contact__hero": { title: "contactPage.title", subtitle: "contactPage.subtitle" },
  "contact__form": { title: "contactPage.form.title" },
  "contact__info": { title: "contactPage.office.title", body: "contactPage.office.addressValue" },
  "contact__multilingual": { title: "contactPage.lang.title", body: "contactPage.lang.desc" },
};

export default function useCmsBridge(currentLang: string) {
  useEffect(() => {
    const fetchAndApply = async () => {
      // Always fetch both the active language AND English (for English CMS overrides)
      const langsToFetch = currentLang === "en" ? ["en"] : [currentLang, "en"];
      const { data, error } = await supabase
        .from("cms_content")
        .select("page, section, content, language")
        .in("language", langsToFetch);

      if (error) {
        console.error("CMS bridge fetch error:", error);
        return;
      }
      if (!data || data.length === 0) return;

      const overrides: Record<string, string> = {};

      // Apply English entries first (base), then active language on top (override)
      const sorted = [...(data as any[])].sort((a, b) => {
        if (a.language === "en" && b.language !== "en") return -1;
        if (a.language !== "en" && b.language === "en") return 1;
        return 0;
      });

      sorted.forEach((row) => {
        const mapKey = `${row.page}__${row.section}`;
        const fieldMap = CMS_TO_I18N_MAP[mapKey];
        if (!fieldMap || !row.content) return;

        Object.entries(fieldMap).forEach(([cmsField, i18nKey]) => {
          const value = row.content[cmsField];
          if (value !== undefined && value !== null) {
            const isStr = typeof value === "string";
            if (isStr && value.trim() === "") return;
            overrides[i18nKey] = isStr ? value : JSON.stringify(value);
          }
        });
      });

      if (Object.keys(overrides).length === 0) return;

      // Use addResource for dot-separated keys — this correctly sets nested paths
      Object.entries(overrides).forEach(([key, value]) => {
        i18n.addResource(currentLang, "common", key, value);
      });

      // Trigger a re-render of all useTranslation consumers
      i18n.emit("languageChanged", currentLang);
    };

    // Fetch immediately on mount / language change
    fetchAndApply();

    // Real-time subscriptions: listen to active language AND English CMS changes
    const channel = supabase
      .channel(`cms_changes_${currentLang}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cms_content",
          filter: `language=eq.${currentLang}`,
        },
        () => { fetchAndApply(); }
      )
      .subscribe();

    // Also subscribe to English changes so English CMS edits reflect immediately
    const enChannel = currentLang !== "en"
      ? supabase
          .channel(`cms_changes_en_for_${currentLang}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "cms_content",
              filter: `language=eq.en`,
            },
            () => { fetchAndApply(); }
          )
          .subscribe()
      : null;

    return () => {
      supabase.removeChannel(channel);
      if (enChannel) supabase.removeChannel(enChannel);
    };
  }, [currentLang]);
}